import { ChangeDetectionStrategy, Component, HostListener, computed, input, output } from '@angular/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

import type { CustomButtonEventPayload } from '@components/sp-custom-button';

import type { LoginEduButtonLinkLegacy, LoginEduDefaultButtonLegacy } from '@sites/entity-core';
import type { LoginEduForm } from '@sites/entity-signal-form';

import type { EButtonLinkType } from '@common/enums';
import { EButtonStyleType } from '@common/enums';
import { isEmptyButtonLabel } from '@common/helpers/forms';
import { ERadioGroupType } from '@ui/sp-radio-group';

import {
    BUTTON_LINK_TYPE_PLACEHOLDER,
    LINK_TYPE_NAME,
    LINK_TYPES,
} from '@web/widgets/button/constants/button-widget.const';
import { WebCustomButtonComponent } from '@web/shared/components/web-custom-button/web-custom-button.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Styling panel for a free-form button. Reads and writes `buttons[index]`
 * straight on the widget signal form; the parent control owns the transaction —
 * save/cancel are emitted up.
 */
@Component({
    selector: 'web-default-button-settings',
    templateUrl: './default-button-settings.component.html',
    styleUrls: ['./default-button-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, BsDropdownModule, WebCustomButtonComponent],
})
export class DefaultButtonSettingsComponent {
    public readonly form = input.required<LoginEduForm>();
    public readonly index = input.required<number>();

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    public readonly EButtonStyleType = EButtonStyleType;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly LINK_TYPE_NAME = LINK_TYPE_NAME;
    public readonly LINK_TYPES = LINK_TYPES;
    public readonly BUTTON_LINK_TYPE_PLACEHOLDER = BUTTON_LINK_TYPE_PLACEHOLDER;

    private readonly button = computed<LoginEduDefaultButtonLegacy | undefined>(
        () => this.form().buttons().value()[this.index()] as LoginEduDefaultButtonLegacy | undefined
    );

    public readonly label = computed(() => this.button()?.label ?? '');
    public readonly styleType = computed(() => this.button()?.styleType);
    public readonly size = computed(() => this.button()?.size);
    public readonly linkType = computed(() => this.button()?.link?.type as EButtonLinkType);
    public readonly linkUrl = computed(() => this.button()?.link?.url ?? '');
    public readonly isTargetBlank = computed(() => Boolean(this.button()?.isTargetBlank));
    public readonly buttonBase = computed(() => this.button()?.buttonBase);
    public readonly buttonHover = computed(() => this.button()?.buttonHover);
    public readonly isIndependentCorners = computed(() => Boolean(this.button()?.isIndependentCorners));
    public readonly advancedRadius = computed(() => this.button()?.advancedRadius);
    public readonly isCustomStyle = computed(() => this.styleType() === EButtonStyleType.custom);

    /** Blank button text is invalid — Save and the Enter shortcut are gated on it. */
    public readonly isEmptyLabel = computed(() => isEmptyButtonLabel(this.label()));

    // --- Setters (template event handlers) ---

    public onLabelChange(event: Event): void {
        this.patch({ label: (event.target as HTMLInputElement).value });
    }

    public onStyleTypeChange(event: Event): void {
        this.patch({
            styleType: (event.target as HTMLSelectElement).value as LoginEduDefaultButtonLegacy['styleType'],
        });
    }

    public setSize(value: string): void {
        this.patch({ size: value as LoginEduDefaultButtonLegacy['size'] });
    }

    public setLinkType(linkType: { name: string; type: EButtonLinkType }): void {
        if (linkType.type === this.linkType()) {
            return;
        }

        this.patchLink({ type: linkType.type as LoginEduButtonLinkLegacy['type'] }); // TODO: RESET OTHER VALUES
    }

    public onLinkUrlChange(event: Event): void {
        this.patchLink({ url: (event.target as HTMLInputElement).value });
    }

    public toggleTargetBlank(value: boolean): void {
        this.patch({ isTargetBlank: value });
    }

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        const patch: Partial<LoginEduDefaultButtonLegacy> = {
            buttonBase: changes.base as LoginEduDefaultButtonLegacy['buttonBase'],
            buttonHover: changes.hover as LoginEduDefaultButtonLegacy['buttonHover'],
        };

        if (changes?.isIndependentCorners !== undefined) {
            patch.isIndependentCorners = changes.isIndependentCorners;
        }
        if (changes?.radiusSettings) {
            patch.advancedRadius = changes.radiusSettings as LoginEduDefaultButtonLegacy['advancedRadius'];
        }

        this.patch(patch);
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        if (this.isEmptyLabel()) return;

        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }

    private patchLink(patch: Partial<LoginEduButtonLinkLegacy>): void {
        const button = this.button();
        if (!button) return;

        this.patch({ link: { ...button.link, ...patch } });
    }

    private patch(patch: Partial<LoginEduDefaultButtonLegacy>): void {
        this.form().updateButton(this.index(), patch);
    }
}
